/**
 * Processing Stats Card Component
 *
 * Displays comprehensive statistics dashboard for email processing,
 * including declaration emails, ATLAS submissions, and processing metrics
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  LinearProgress,
  Divider,
  Chip,
  useTheme,
} from '@mui/material';
import {
  Email as EmailIcon,
  AttachFile as AttachmentIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Schedule as PendingIcon,
  Description as DocumentIcon,
  CloudUpload as ApiIcon,
  Speed as SpeedIcon,
  AssignmentTurnedIn as DeclarationIcon,
  HowToReg as VerifiedIcon,
  Lock as AuthIcon,
  TrendingUp as TrendIcon,
} from '@mui/icons-material';

import { format } from 'date-fns';

import { ProcessingStats } from '../../types/emailProcessor';
import emailProcessorService from '../../services/emailProcessorService';
import emailProcessingService from '../../services/emailProcessingService';

interface EmailProcessorStatsCardProps {
  onError: (error: string) => void;
}

interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color?: string;
  subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, subtitle }) => {
  const theme = useTheme();
  const finalColor = color || theme.palette.primary.main;

  return (
    <Card sx={{ height: '100%', minHeight: 140 }}>
      <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom noWrap>
              {title}
            </Typography>
            <Typography variant="h4" component="div" sx={{ color: finalColor }}>
              {value}
            </Typography>
          </Box>
          <Box sx={{ color, opacity: 0.5 }}>{icon}</Box>
        </Box>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 'auto', pt: 1, display: 'block' }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const ProcessingStatsCard: React.FC<EmailProcessorStatsCardProps> = ({ onError }) => {
  const theme = useTheme();
  const [stats, setStats] = useState<ProcessingStats | null>(null);
  const [declarationStats, setDeclarationStats] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadStats();
    const interval = setInterval(loadStats, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const loadStats = async () => {
    setLoading(true);
    try {
      // Load general processing stats
      const data = await emailProcessorService.getProcessingStats();
      setStats(data);

      // Also try to load declaration-specific stats from email history
      try {
        const historyResponse = await emailProcessingService.getHistory(1, 100);

        // Calculate declaration stats
        const declarationThreads = historyResponse.email_threads.filter((t: any) => t.is_declaration);
        const atlasSubmitted = declarationThreads.filter((t: any) => t.atlas_submission_response).length;

        // Count by declaration state
        const stateCountsMap: { [key: string]: number } = {};
        declarationThreads.forEach((thread: any) => {
          const state = thread.declaration_state || 'unknown';
          stateCountsMap[state] = (stateCountsMap[state] || 0) + 1;
        });

        setDeclarationStats({
          total_declarations: declarationThreads.length,
          atlas_submitted: atlasSubmitted,
          by_state: stateCountsMap,
          success_rate: declarationThreads.length > 0
            ? Math.round((atlasSubmitted / declarationThreads.length) * 100)
            : 0
        });
      } catch (err) {
        // If email history fails, just use the basic stats
        console.error('Could not load declaration stats:', err);
      }
    } catch (error: any) {
      onError(error.response?.data?.detail || 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !stats) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!stats) {
    return null;
  }

  const successRate =
    stats.total_attachments > 0
      ? Math.round((stats.successful_attachments / stats.total_attachments) * 100)
      : 0;

  const apiSuccessRate =
    stats.api_submitted > 0 ? Math.round((stats.api_success / stats.api_submitted) * 100) : 0;

  return (
    <Box>
      {/* Overview Stats */}
      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        {"Processing Overview"}
      </Typography>

      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={6} md={3} lg={3}>
          <StatCard
            title={"Total Emails"}
            value={stats.total_emails}
            icon={<EmailIcon sx={{ fontSize: 40 }} />}
            color={theme.palette.primary.main}
          />
        </Grid>

        <Grid item xs={6} sm={6} md={3} lg={3}>
          <StatCard
            title={"Total Attachments"}
            value={stats.total_attachments}
            icon={<AttachmentIcon sx={{ fontSize: 40 }} />}
            color={theme.palette.info.main}
          />
        </Grid>

        <Grid item xs={6} sm={6} md={3} lg={3}>
          <StatCard
            title={"Successful"}
            value={stats.successful_attachments}
            icon={<SuccessIcon sx={{ fontSize: 40 }} />}
            color={theme.palette.success.main}
            subtitle={`${successRate}% success rate`}
          />
        </Grid>

        <Grid item xs={6} sm={6} md={3} lg={3}>
          <StatCard
            title={"Failed"}
            value={stats.failed_attachments}
            icon={<ErrorIcon sx={{ fontSize: 40 }} />}
            color={theme.palette.error.main}
          />
        </Grid>

        <Grid item xs={6} sm={6} md={3} lg={3}>
          <StatCard
            title={"Pending"}
            value={stats.pending_attachments}
            icon={<PendingIcon sx={{ fontSize: 40 }} />}
            color={theme.palette.warning.main}
          />
        </Grid>

        <Grid item xs={6} sm={6} md={3} lg={3}>
          <StatCard
            title={"Avg Processing Time"}
            value={emailProcessorService.formatProcessingTime(stats.average_processing_time_ms)}
            icon={<SpeedIcon sx={{ fontSize: 40 }} />}
            color={theme.palette.secondary.main}
          />
        </Grid>

        <Grid item xs={6} sm={6} md={3} lg={3}>
          <StatCard
            title={"API Submitted"}
            value={stats.api_submitted}
            icon={<ApiIcon sx={{ fontSize: 40 }} />}
            color={theme.palette.primary.main}
            subtitle={`${apiSuccessRate}% success rate`}
          />
        </Grid>

        <Grid item xs={6} sm={6} md={3} lg={3}>
          <StatCard
            title={"API Success"}
            value={stats.api_success}
            icon={<SuccessIcon sx={{ fontSize: 40 }} />}
            color={theme.palette.success.main}
          />
        </Grid>
      </Grid>

      {/* Document Type Breakdown */}
      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        {"Document Types"}
      </Typography>

      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={6} md={3} lg={3}>
          <StatCard
            title={"Invoices"}
            value={stats.invoices}
            icon={<DocumentIcon sx={{ fontSize: 40 }} />}
          />
        </Grid>

        <Grid item xs={6} sm={6} md={3} lg={3}>
          <StatCard
            title={"Packing Lists"}
            value={stats.packing_lists}
            icon={<DocumentIcon sx={{ fontSize: 40 }} />}
          />
        </Grid>

        <Grid item xs={6} sm={6} md={3} lg={3}>
          <StatCard
            title={"Certificates"}
            value={stats.certificates}
            icon={<DocumentIcon sx={{ fontSize: 40 }} />}
          />
        </Grid>

        <Grid item xs={6} sm={6} md={3} lg={3}>
          <StatCard
            title={"Other Documents"}
            value={stats.other_documents}
            icon={<DocumentIcon sx={{ fontSize: 40 }} />}
          />
        </Grid>
      </Grid>

      {/* Declaration Email Statistics */}
      {declarationStats && declarationStats.total_declarations > 0 && (
        <>
          <Typography variant="h6" gutterBottom sx={{ mb: 3, mt: 4 }}>
            {"Declaration Emails"}
          </Typography>

          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={6} sm={6} md={3} lg={3}>
              <StatCard
                title={"Total Declarations"}
                value={declarationStats.total_declarations}
                icon={<DeclarationIcon sx={{ fontSize: 40 }} />}
                color="primary.main"
              />
            </Grid>

            <Grid item xs={6} sm={6} md={3} lg={3}>
              <StatCard
                title={"ATLAS Submitted"}
                value={declarationStats.atlas_submitted}
                icon={<ApiIcon sx={{ fontSize: 40 }} />}
                color="success.main"
                subtitle={`${declarationStats.success_rate}% success rate`}
              />
            </Grid>

            <Grid item xs={6} sm={6} md={3} lg={3}>
              <StatCard
                title={"Awaiting Documents"}
                value={declarationStats.by_state?.AWAITING_DOCUMENTS || 0}
                icon={<AttachmentIcon sx={{ fontSize: 40 }} />}
                color="warning.main"
              />
            </Grid>

            <Grid item xs={6} sm={6} md={3} lg={3}>
              <StatCard
                title={"Awaiting Auth"}
                value={declarationStats.by_state?.AUTH_REQUIRED || 0}
                icon={<AuthIcon sx={{ fontSize: 40 }} />}
                color="info.main"
              />
            </Grid>
          </Grid>

          {/* Declaration State Breakdown */}
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              {"Declaration Status Breakdown"}
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {Object.entries(declarationStats.by_state || {}).map(([state, count]) => (
                <Grid item key={state}>
                  <Chip
                    label={`${state.replace(/_/g, ' ')}: ${count}`}
                    color={
                      state === 'SUBMITTED' ? 'success' :
                      state === 'FAILED' ? 'error' :
                      state === 'PROCESSING' ? 'warning' :
                      'default'
                    }
                    variant="outlined"
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>
        </>
      )}

      {/* Success Rate Progress */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {"Processing Success Rates"}
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">
              {"Processing Success"}
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {successRate}%
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={successRate} sx={{ height: 10, borderRadius: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {stats.successful_attachments} of {stats.total_attachments} attachments processed successfully
          </Typography>
        </Box>

        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">{"API Success"}</Typography>
            <Typography variant="body2" fontWeight="bold">
              {apiSuccessRate}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={apiSuccessRate}
            color="success"
            sx={{ height: 10, borderRadius: 1 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {stats.api_success} of {stats.api_submitted} API submissions successful
          </Typography>
        </Box>

        {stats.last_processed_at && (
          <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary">
              {"Last processed"}:{' '}
              {format(new Date(stats.last_processed_at), 'PPp')}
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ProcessingStatsCard;
